import React, { useMemo, useState } from "react";

import ClearIcon from "@mui/icons-material/Clear";
import { Stack } from "@mui/system";
import Decimal from "decimal.js";
import { orderBy } from "lodash";

import useGetPortfolioAssignmentClassificationById from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioAssignmentClassificationById";
import useGetPortfolioPlanClassificationById from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioPlanClassificationById";
import usePostPortfolioPlanRenderTree from "@fartherfinance/frontend/api/Investment/hooks/usePostPortfolioPlanRenderTree";
import { ClientId, PortfolioPlanId } from "@fartherfinance/frontend/api/Types";

import {
  TableContainerV2,
  TableHeaderV2,
} from "../../Client/Portfolio/shared/shared";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import BasicTableChip from "@src/sharedComponents/BasicTableChip/BasicTableChip";
import { BorderedRow } from "@src/sharedComponents/List/BorderedList";
import Text from "@src/sharedComponents/Typography/Text";
import { pluralize } from "@src/utils/pluralize";

import AssignmentsDropDown, {
  Assignment,
  PortfolioPlanUnion,
} from "./AssignmentsDropDown";
import {
  countAllSecurities,
  getAllSecurities,
  getCurrentLevel,
  PortfolioData,
} from "./classificationTableHelpers";
import ErrorState from "./ErrorState";
import formatFinMasonError from "./formatFinMasonError";
import LoadingState from "./LoadingState";
import NestedPlanTable from "./NestedPlanTable";
import SecurityRows from "./SecurityRows";
import SubtleSpan from "./SubtleSpan";

import styles from "./Classification.module.css";

type Props = {
  portfolioPlanId: PortfolioPlanId;
  clientId?: ClientId;
};

type SecurityView =
  | "ClassificationGrouped"
  | "ClassificationFlat"
  | "NestedPlans";

type SortBy = "name" | "weight";

const ClassificationTable: React.FC<Props> = (props) => {
  const [portfolioAssignment, setPortfolioAssignment] = useState<
    Assignment | undefined
  >(undefined);

  const selectPortfolioAssignment = (portfolio: PortfolioPlanUnion) => {
    if (portfolio.type === "PortfolioPlanAssignment") {
      setPortfolioAssignment(portfolio);
      return;
    }
    setPortfolioAssignment(undefined);
  };

  const auth = useAdvisorRequestAuth();
  const clientAuth = useRequestAuth();

  const portfolioPlanClassificationData = useGetPortfolioPlanClassificationById(
    props.portfolioPlanId,
    auth ?? clientAuth
  );

  const assignmentClassificationData =
    useGetPortfolioAssignmentClassificationById(
      props.portfolioPlanId,
      portfolioAssignment?.id ?? null,
      auth
    );

  // if any of rows have "NestedPlan", we enable the show sleeves button
  // because the render tree and the assignment render tree have same structure
  // we don't need to call both to check if there are nested plans
  const nestedPlans = usePostPortfolioPlanRenderTree(
    props.portfolioPlanId,
    auth ?? clientAuth
  );

  const shouldShowSleeves: boolean = useMemo(() => {
    if (nestedPlans.data === undefined) {
      return false;
    }
    return nestedPlans.data.some(
      (nestedPlan) => nestedPlan.type === "NestedPlan"
    );
  }, [nestedPlans.data]);

  const [categoriesSortedBy, setCategoriesSortedBy] = useState<SortBy>("name");
  const [securitiesSortedBy, setSecuritiesSortedBy] = useState<SortBy>("name");

  const [path, setPath] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<SecurityView>(
    "ClassificationGrouped"
  );

  // if there's a selected assignment, we should use the assignment data instead of the portfolio plan data
  const dataClassification = portfolioAssignment?.id
    ? assignmentClassificationData
    : portfolioPlanClassificationData;

  const entries = useMemo(
    () => getCurrentLevel(dataClassification.data, path),
    [dataClassification.data, path]
  );

  const sortedCategories = useMemo(() => {
    if (entries === null || entries?.type === "Securities") {
      return undefined;
    }
    switch (categoriesSortedBy) {
      case "name":
        // we don't expect name to be duplicated, so sorting only by name is fine
        return orderBy(entries?.entries, [(entry) => entry?.category], ["asc"]);

      case "weight":
        return orderBy(
          entries.entries,
          [(entry) => entry.weight.toNumber(), (entry) => entry.category],
          ["desc", "asc"]
        );
    }
  }, [categoriesSortedBy, entries]);

  if (dataClassification.isLoading) {
    return (
      <LoadingState
        leftTitle={"Holdings and Securities"}
        rightTitle={"Loading"}
        leftColumn={"Category"}
        rightColumn={"Weight"}
      />
    );
  }

  if (dataClassification.hasError) {
    const errorMessage = formatFinMasonError(dataClassification.error);

    return (
      <ErrorState
        leftTitle={"Holdings and Securities"}
        rightTitle={""}
        errorMessage={errorMessage}
      />
    );
  }

  if (entries === null) {
    return null;
  }

  const goBack = () => setPath(path.slice(0, -1));

  if (entries.type === "Securities") {
    return (
      <TableContainerV2>
        <TableHeaderV2
          leftTitle={path[path.length - 1]}
          rightTitle={
            props.clientId ? (
              <AssignmentsDropDown
                portfolioPlanId={props.portfolioPlanId}
                curItem={portfolioAssignment?.label}
                onSelect={selectPortfolioAssignment}
              />
            ) : (
              `${pluralize(entries.entries.length, "Security", true)}`
            )
          }
          onBack={goBack}
        />
        <div style={{ paddingLeft: 10, paddingRight: 10 }}>
          <BorderedRow
            left={
              <SubtleSpan onClick={() => setSecuritiesSortedBy("name")}>
                {"Category"}
              </SubtleSpan>
            }
            right={
              <SubtleSpan onClick={() => setSecuritiesSortedBy("weight")}>
                {"Weight"}
              </SubtleSpan>
            }
          />
          <SecurityRows
            sortedBy={securitiesSortedBy}
            securities={entries.entries}
          />
          <BorderedRow
            removeBorderBottom
            left=""
            right={
              <Stack direction="row">
                <SubtleSpan style={{ paddingRight: 10 }}>Total </SubtleSpan>
                <Text>
                  {entries.weight.toNumber().toLocaleString("en-US", {
                    style: "percent",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Text>
              </Stack>
            }
          />
        </div>
      </TableContainerV2>
    );
  }

  if (viewMode === "ClassificationFlat") {
    return (
      <AllSecuritiesTable
        data={dataClassification.data}
        clearAllSecurities={() => setViewMode("ClassificationGrouped")}
        portfolioPlanId={props.portfolioPlanId}
        onSelect={selectPortfolioAssignment}
        curItem={portfolioAssignment?.label}
        clientId={props.clientId}
      />
    );
  }

  if (viewMode === "NestedPlans") {
    return (
      <NestedPlanTable
        portfolioPlanId={props.portfolioPlanId}
        resetView={() => setViewMode("ClassificationGrouped")}
        onSelect={selectPortfolioAssignment}
        curItem={portfolioAssignment?.label}
        clientId={props.clientId}
        portfolioPlanAssignment={portfolioAssignment?.id}
      />
    );
  }

  const currentLevel: string =
    path[path.length - 1] ?? "Holdings and Securities";

  const count = countAllSecurities(dataClassification.data);
  // if path is empty, we are at the top level
  // and we want to sum all the counts
  // otherwise, we are gonna use the key to access the count per category
  const totalSecurities =
    path.length === 0
      ? Object.values(count).reduce((acc, curr) => acc + curr, 0)
      : count[path[path.length - 1]];

  return (
    <TableContainerV2>
      <TableHeaderV2
        leftTitle={
          <Stack direction={"row"} gap={1}>
            <Text>{currentLevel}</Text>
            {props.clientId ? (
              <Text color="textSubtle">{`${pluralize(
                totalSecurities,
                "Security",
                true
              )}`}</Text>
            ) : null}
          </Stack>
        }
        rightTitle={
          props.clientId ? (
            <AssignmentsDropDown
              portfolioPlanId={props.portfolioPlanId}
              curItem={portfolioAssignment?.label}
              onSelect={selectPortfolioAssignment}
            />
          ) : (
            `${pluralize(totalSecurities, "Security", true)}`
          )
        }
        onBack={path.length === 0 ? undefined : goBack}
      />
      {path.length === 0 ? (
        <BorderedRow
          left={
            <Stack direction="row">
              <BasicTableChip
                text={"Show All Securities"}
                isSelected={false}
                onClick={() =>
                  setViewMode(
                    viewMode === "ClassificationGrouped"
                      ? "ClassificationFlat"
                      : "ClassificationGrouped"
                  )
                }
              />
              <BasicTableChip
                isDisabled={shouldShowSleeves === false}
                text={"Show Sleeves"}
                isSelected={false}
                onClick={() =>
                  setViewMode(
                    viewMode === "ClassificationGrouped"
                      ? "NestedPlans"
                      : "ClassificationGrouped"
                  )
                }
              />
            </Stack>
          }
          right={""}
        />
      ) : null}
      <div style={{ paddingLeft: 10, paddingRight: 10 }}>
        <BorderedRow
          left={
            <SubtleSpan onClick={() => setCategoriesSortedBy("name")}>
              {"Category"}
            </SubtleSpan>
          }
          right={
            <SubtleSpan onClick={() => setCategoriesSortedBy("weight")}>
              {"Weight"}
            </SubtleSpan>
          }
        />

        {sortedCategories?.map((cat) => {
          const weight = cat.weight.toNumber().toLocaleString("en-US", {
            style: "percent",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });

          return (
            <BorderedRow
              key={cat.category}
              left={cat.category}
              right={weight}
              onClick={() => setPath([...path, cat.category])}
            />
          );
        })}

        <BorderedRow
          left=""
          removeBorderBottom
          right={
            <Stack direction="row">
              <SubtleSpan style={{ paddingRight: 10 }}>Total </SubtleSpan>
              <Text>
                {entries.weight.toNumber().toLocaleString("en-US", {
                  style: "percent",
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Text>
            </Stack>
          }
        />
      </div>
    </TableContainerV2>
  );
};

export default ClassificationTable;

type AllSecuritiesTableProps = {
  data: PortfolioData;
  clearAllSecurities: () => void;
  portfolioPlanId: PortfolioPlanId;
  onSelect: (el: PortfolioPlanUnion) => void;
  curItem?: string;
  clientId?: ClientId;
};

// will receive all the data and display the securities all together
const AllSecuritiesTable: React.FC<AllSecuritiesTableProps> = (props) => {
  const [securitiesSortedBy, setSecuritiesSortedBy] = useState<SortBy>("name");

  const allSecurities = useMemo(
    () => getAllSecurities(props.data),
    [props.data]
  );

  return (
    <TableContainerV2>
      <TableHeaderV2
        leftTitle={
          <Stack direction={"row"} gap={1}>
            <Text>All Securities</Text>
            {props.clientId ? (
              <Text color="textSubtle">{`
              ${pluralize(allSecurities.length, "Security", true)}`}</Text>
            ) : null}
          </Stack>
        }
        rightTitle={
          props.clientId ? (
            <AssignmentsDropDown
              portfolioPlanId={props.portfolioPlanId}
              curItem={props.curItem}
              onSelect={props.onSelect}
            />
          ) : (
            `${pluralize(allSecurities.length, "Security", true)}`
          )
        }
      />
      <BorderedRow
        left={
          <BasicTableChip
            text={"Show All Securities"}
            isSelected={true}
            onClick={props.clearAllSecurities}
          />
        }
        right={
          <div className={styles.clearPill} onClick={props.clearAllSecurities}>
            <div className={styles.clearPillText}>Clear</div>
            <ClearIcon className={styles.clearPillIcon} />
          </div>
        }
      />
      <div style={{ paddingLeft: 10, paddingRight: 10 }}>
        <BorderedRow
          left={
            <SubtleSpan onClick={() => setSecuritiesSortedBy("name")}>
              {"Security"}
            </SubtleSpan>
          }
          right={
            <SubtleSpan onClick={() => setSecuritiesSortedBy("weight")}>
              {"Weight"}
            </SubtleSpan>
          }
        />
        <SecurityRows
          sortedBy={securitiesSortedBy}
          securities={allSecurities}
        />
      </div>
      <BorderedRow
        left=""
        removeBorderBottom
        right={
          <Stack direction="row">
            <SubtleSpan style={{ paddingRight: 10 }}>Total </SubtleSpan>
            <Text>
              {allSecurities
                .reduce(
                  (acc, curr) => Decimal.sum(acc, curr.weight),
                  new Decimal(0)
                )
                .toNumber()
                .toLocaleString("en-US", {
                  style: "percent",
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
            </Text>
          </Stack>
        }
      />
    </TableContainerV2>
  );
};
